import { useState, useEffect, useCallback } from "react";
import { View } from "react-native-web";
import CustomText from "../../components/atoms/CustomText";
import { spacing, colors } from "../../utils/styling";

import { useResponsive } from "../../hooks/useResponsive";
import { useLocation, useNavigate } from "react-router-dom";

import {
  createCheckoutSession,
  sendPdfEmail,
  trackPDFDownload,
} from "../../lib/stripe";
import PaymentOverlay from "./PaymentOverlay";
import * as Toast from "@radix-ui/react-toast";
import { generatePDFBlob } from "../../lib/pdf-utils";
import { uploadPDFToSupabaseSimple } from "../../lib/supabase-pdf";
import {
  CATEGORY_SECTIONS,
  SPECIFIC_CATEGORY_SECTIONS,
} from "../../utils/category_mappings";
import { getResultsDataFromStateOrStorage, ResultsData } from "../../utils/resultsData";
import CircularProgress from "@mui/material/CircularProgress";


function checkValidCode(code: string): boolean {
  // TODO: Replace with real logic
  return code.trim().toLowerCase() === "secretcode1234";
}

// Helper to handle post-signup success (PDF, email, navigation) - FREE VERSION
async function handlePostSignupSuccess({
  email,
  quizId,
  resultsData,
  safeResultsData,
  navigate,
  location,
  CATEGORY_SECTIONS,
  SPECIFIC_CATEGORY_SECTIONS,
  setShowPaymentProcessingOverlay,
}: any) {
  try {
    console.log("🎉 Free access - generating PDF and sending email");

    // Set processing overlay
    setShowPaymentProcessingOverlay(true);

    // Generate PDF
    const pdfBlob = await generatePDFBlob({
      answers: resultsData.answers || [],
      categoryScores: resultsData.categoryScores || {},
      dominantCategory: resultsData.dominantCategory || "",
      personalInfo: {},
    });

    // Upload PDF to Supabase
    const emailHash = email ? email.replace(/[^a-zA-Z0-9]/g, "") : "user";
    const dateStr = new Date().toISOString().split("T")[0];
    const filename = `personality-profile-${emailHash}-${dateStr}`;
    let url: string | undefined;
    try {
      const uploadResult = await uploadPDFToSupabaseSimple(pdfBlob, filename);
      url = uploadResult.url;
    } catch (uploadError) {
      // Fallback: Download PDF locally for testing
      const localUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = localUrl;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(localUrl);
      setShowPaymentProcessingOverlay(false);
      return;
    }
    // Track PDF download via backend
    if (quizId && email && url) {
      try {
        await trackPDFDownload(quizId, email, filename, url);
      } catch (trackError) {
        // non-critical
      }
    }
    // Call the backend to send email with PDF URL.
    // The email is a non-critical side effect (like trackPDFDownload above): if it fails
    // — e.g. SendGrid isn't fully configured yet — the user must STILL reach their results.
    // Never let a failed email leave the "Setting things up..." overlay spinning forever.
    if (email && quizId && url) {
      try {
        await sendPdfEmail(
          email,
          quizId,
          resultsData,
          "FREE-MONTH",
          CATEGORY_SECTIONS,
          SPECIFIC_CATEGORY_SECTIONS,
          resultsData.dominantCategory,
          resultsData.specificDominantCategory,
          url
        );
      } catch (emailError) {
        // non-critical — the report is still in localStorage and shown on the results page
        console.warn("⚠️ PDF email failed (non-critical), continuing to results:", emailError);
      }
    }

    // Navigate to SignUp with payment confirmation and results data
    navigate("/signup", {
      state: {
        ...safeResultsData,
        paymentConfirmed: true,
        from: location.pathname,
      },
    });
  } catch (error) {
    console.error("❌ Error in free access flow:", error);
  } finally {
    // Always clear the processing overlay so the user is never stuck on the spinner.
    setShowPaymentProcessingOverlay(false);
  }
}

export default function UnlockAndSignUpTemp() {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();

  // Unified loader for results data
  let resultsData = getResultsDataFromStateOrStorage(location.state);

  const defaultResultsData: ResultsData = {
    answers: [],
    categoryScores: {},
    dominantCategory: "",
    sfAccount: null,
    quizId: undefined,
    leadId: undefined,
    email: undefined,
  };

  // Use resultsData if available, otherwise use defaults
  const safeResultsData = resultsData || defaultResultsData;
  const { email, sfAccount, quizId } = safeResultsData;

  // State for free access flow
  const [clicked, setClicked] = useState(false);
  const [showPaymentProcessingOverlay, setShowPaymentProcessingOverlay] =
    useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [error, setError] = useState(false);

  const handleGetFreeAccess = async () => {
    if (!email || !quizId) {
      console.error("Missing email or quizId for free access");
      return;
    }

    setClicked(true);

    try {
      await handlePostSignupSuccess({
        email,
        quizId,
        resultsData: safeResultsData,
        safeResultsData,
        navigate,
        location,
        CATEGORY_SECTIONS,
        SPECIFIC_CATEGORY_SECTIONS,
        setShowPaymentProcessingOverlay,
      });
    } catch (error) {
      console.error("Error in free access flow:", error);
      setClicked(false);
    }
  };

  return (
    <Toast.Provider>
      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: isMobile ? spacing.md : spacing.xl,
          paddingLeft: isMobile ? spacing.lg : spacing.xl,
          paddingRight: isMobile ? spacing.lg : spacing.xl,
          marginBottom: spacing.xxl,
        }}
      >
        <View
          style={{
            width: "100%",
            maxWidth: 1400,
            paddingTop: isMobile ? spacing.lg : spacing.lg * 1.25,
            paddingBottom: isMobile ? spacing.xxl : 0,
          }}
        >
          {/* Card for heading and subtitle */}
          <View
            style={{
              backgroundColor: colors.white,
              borderRadius: 16,
              padding: isMobile ? spacing.md : spacing.xl,
              marginBottom: spacing.xl,
              width: "100%",
              alignItems: "center",
              border: "3px solid black",
            }}
          >
            <CustomText
              style={{
                color: colors.dark,
                textAlign: "center",
                width: "100%",
                fontFamily: "Nunito",
                fontWeight: 1000,
                fontSize: isMobile ? 30 : 65,
                lineHeight: "1.1",
                marginBottom: isMobile ? spacing.md : spacing.lg,
              }}
            >
              See your full personality profile.
            </CustomText>
            <CustomText
              style={{
                color: colors.dark,
                textAlign: "center",
                width: "100%",
                fontFamily: "Nunito",
                fontWeight: 1000,
                fontSize: isMobile ? 24 : 36,
                lineHeight: "1.1",
                marginBottom: isMobile ? spacing.md : spacing.lg,
              }}
            >
              Your results are ready.
            </CustomText>
            <img
              src={require("../../components/img/example-results.png")}
              alt="Example Results Page"
              style={{
                height: isMobile ? 300 : 600,
                width: "auto",
                marginBottom: isMobile ? spacing.md : spacing.lg,
              }}
            />

            <CustomText
              style={{
                fontFamily: "Nunito",
                marginBottom: isMobile ? spacing.sm : spacing.md,
              }}
            >
              Free access to your complete results.
            </CustomText>
            <button
              onClick={handleGetFreeAccess}
              disabled={clicked}
              style={{
                color: "white",
                backgroundColor: clicked ? colors.gray[400] : colors.primary,
                borderRadius: 50,
                padding: isMobile
                  ? `${spacing.md}px ${spacing.xl}px`
                  : `${spacing.md}px ${spacing.xl * 2}px`,
                fontFamily: "Nunito",
                fontSize: isMobile ? 18 : 28,
                fontWeight: "600",
                cursor: clicked ? "not-allowed" : "pointer",
                transition: "all 0.3s ease",
                minWidth: 250,
                marginBottom: isMobile ? spacing.md : spacing.lg,
                opacity: clicked ? 0.6 : 1,
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: spacing.sm,
              }}
              onMouseEnter={(e) => {
                if (!clicked) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.opacity = "0.85";
                }
              }}
              onMouseLeave={(e) => {
                if (!clicked) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.opacity = "1";
                }
              }}
            >
              {clicked ? (
                <>
                  <CircularProgress size={24} color="inherit" />
                  <span>Processing...</span>
                </>
              ) : (
                "Get My FREE Results Now!"
              )}
            </button>
            {error && (
              <CustomText
                style={{
                  color: colors.danger,
                  fontSize: isMobile ? 16 : 20,
                  marginBottom: isMobile ? spacing.md : spacing.lg,
                  textAlign: "center",
                }}
              >
                {error}
              </CustomText>
            )}
            <CustomText
              style={{
                fontFamily: "Nunito",
                fontSize: isMobile ? 14 : 16,
                color: colors.gray[600],
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              No payment required • Instant access • Full report emailed to you
            </CustomText>
          </View>
        </View>

        {/* Payment Processing Overlay */}
        {showPaymentProcessingOverlay && (
          <View
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
            }}
          >
            <View
              style={{
                backgroundColor: colors.white,
                borderRadius: 16,
                padding: isMobile ? spacing.lg : spacing.xxl,
                maxWidth: 500,
                width: "90%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: spacing.lg,
              }}
            >
              <CircularProgress size={60} style={{ color: colors.primary }} />
              <CustomText
                style={{
                  fontSize: isMobile ? 24 : 32,
                  fontWeight: "600",
                  color: colors.dark,
                  fontFamily: "Nunito",
                  textAlign: "center",
                  marginBottom: spacing.md,
                }}
              >
                Money Personality Unlocked!
              </CustomText>
              <CustomText
                style={{
                  fontSize: isMobile ? 16 : 20,
                  color: colors.gray[700],
                  fontFamily: "Nunito",
                  textAlign: "center",
                }}
              >
                Setting things up and redirecting...
              </CustomText>
            </View>
          </View>
        )}

        <Toast.Viewport
          style={{
            position: "fixed",
            bottom: 40,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 9999,
          }}
        />
      </View>
    </Toast.Provider>
  );
}
